import { FLOWCODE_ROOT } from '../../constants/root'
import { PostableMessage } from './types'
import { useEffect, useState } from 'react'

type SendPostableMessage = (
  toSend: PostableMessage,
  targetIframeRef: React.RefObject<HTMLIFrameElement>
) => void

/**
 *
 * @param toSend PostableMessage { key, body, origin, targetOrigin }
 * @param targetIframeRef Requires entire object so origin can be vertically accessed
 * @returns Either a validated callback or undefined
 *
 *
 * @example
 * const someMessage = {
 *  key: PostableMessageKey.SOME_KEY,
 *  body: JSON.stringify(someAssetData),
 *  origin: FLOWCODE_ROOT,
 *  targetOrigin: FLOWCODE_ROOT
 * }
 *
 * const iframeRef = useRef<HTMLIFrameElement>(null)
 *
 * useEffect(() => {
 *  const contentWindow = iframeRef?.current?.contentWindow
 *
 *  if(contentWindow) {
 *    const sendMessage = sendPostableMessage(someMessage, contentWindow)
 *    sendMessage()
 *  }
 *
 * }, [iframeRef, sendPostableMessage, someMessage])
 *
 * return <iframe ref={iframeRef} src={SOME_TARGET_ORIGIN} }></iframe>
 */
export const sendPostableMessage: SendPostableMessage = (toSend, targetWindow) => {
  try {
    targetWindow.current?.contentWindow?.postMessage(toSend, toSend.targetOrigin)
  } catch (e) {
    console.error(e)
  }
}

// Sends a postMessage back to the parent window w/ origins flipped and the body set to the key
const sendConfirmation = ({ key, origin, targetOrigin }: PostableMessage): void => {
  const targetWindow = window.parent
  if (targetWindow.postMessage) {
    targetWindow.postMessage(
      {
        key,
        body: key,
        origin: targetOrigin,
        targetOrigin: origin
      },
      origin
    )
  } else {
    console.error('Window object failed to be utilized', window)
  }
}

type ConsumeMessage = (() => PostableMessage) | undefined
type UseReceivePostableMessage = (
  key?: PostableMessage['key'],
  originToListenTo?: PostableMessage['origin'],
  shouldSendConfirmation?: boolean
) => ConsumeMessage

/**
 * @example
 *const consumeMessage = useReceivePostableMessage(
 * PostableMessageKey.SSO_REDIRECT_ASSET_PERSISTENCE
 *)
 *
 *useEffect(() => {
 *  if (consumeMessage) {
 *    setValue(consumeMessage())
 *  }
 * }, [consumeMessage])
 */
export const useReceivePostableMessage: UseReceivePostableMessage = (
  listenForKey,
  listenToOrigin = FLOWCODE_ROOT,
  shouldSendConfirmation = true
) => {
  const [processedMessages, setProcessedMessages] = useState<PostableMessage[]>([])
  const [receivedMessage, setReceivedMessage] = useState<PostableMessage | undefined>(undefined)

  useEffect(() => {
    if (listenForKey && !receivedMessage) {
      const handler = ({ origin: senderOrigin, data }: MessageEvent): void => {
        if (!(data && senderOrigin === listenToOrigin)) return

        const potentialMessage: PostableMessage = data

        const hasAlreadyReceived = shouldSendConfirmation
          ? processedMessages.some(postable => postable.key === potentialMessage.key)
          : false

        if (!hasAlreadyReceived && potentialMessage.body && potentialMessage.key === listenForKey) {
          setReceivedMessage(potentialMessage)
        }
      }

      window.addEventListener('message', handler)
      return () => window.removeEventListener('message', handler)
    }
  }, [listenForKey, listenToOrigin, processedMessages, receivedMessage, shouldSendConfirmation])

  const consumeMessage: ConsumeMessage = receivedMessage
    ? () => {
        if (shouldSendConfirmation) {
          sendConfirmation(receivedMessage)
          setProcessedMessages(prev => [...prev, receivedMessage])
        }
        setReceivedMessage(undefined)
        return receivedMessage
      }
    : undefined

  return consumeMessage
}

type ConsumeConfirmation = (() => true) | undefined
type UsePostMessage = () => [SendPostableMessage, ConsumeConfirmation]

/**
 * A React implementation of postMessage that includes a concurrency confirmation. postMessage should be used in cross-domain, same-client data transfer to our two approved www and app domains.
 *
 * Batch processing is possible, the shorthand recipe involves a parent component that takes an array of PostableMessage and maps it to individual fragment child components that instantiate their own usePostMessage hook. Each child will be synchronous with the order of the array, for example bag[0] will post its message before bag[1], etc.
 *
 * @returns [setMessageToSend, consumeConfirmation]
 *
 * @example
 *
 * const iframeRef = useRef<HTMLIFrameElement>(null)
 * const [status, setStatus] = useState<string>("Unsent")
 * const [sendMessage, consumeConfirmation] = usePostMessage()
 *
 * const someMessage = {
 *  key: PostableMessageKey.SOME_KEY,
 *  body: JSON.stringify(someAssetData),
 *  origin: FLOWCODE_ROOT,
 *  targetOrigin: FLOWCODE_ROOT
 * }
 *
 * const onClick = () => {
 *  sendMessage(someMessage, iframeRef)
 *  setStatus("Message sent, awaiting confirmation")
 * }
 *
 * useEffect(() => {
 *   if(consumeConfirmation) {
 *     consumeConfirmation() // callback will always return true, consumption will cleanup hook state
 *     setStatus("Confirmation received")
 *   }
 * }, [consumeConfirmation])
 *
 * return <>
 *  {status}
 *  <button onClick={}>Send that message</button>
 * </>
 */
const usePostMessage: UsePostMessage = () => {
  const [targetIframeRef, setTargetIframeRef] = useState<
    React.RefObject<HTMLIFrameElement> | undefined
  >()
  const [toSend, setToSend] = useState<PostableMessage | undefined>()
  const [sent, setSent] = useState(false)
  const consumeRelayConfirmation = useReceivePostableMessage(
    toSend?.key,
    toSend?.targetOrigin,
    false
  )
  const [confirmed, setConfirmed] = useState(false)

  const isReadyToSend =
    targetIframeRef && toSend && !sent && !consumeRelayConfirmation && !confirmed
  const isReadyToVerifyConfirmation =
    targetIframeRef && toSend && sent && consumeRelayConfirmation && !confirmed

  useEffect(() => {
    if (isReadyToSend) {
      sendPostableMessage(toSend, targetIframeRef)
      setSent(true)
    }
  }, [confirmed, isReadyToSend, toSend, sent, targetIframeRef])

  useEffect(() => {
    if (isReadyToVerifyConfirmation) {
      const confirmationMessage = consumeRelayConfirmation()

      if (confirmationMessage.body === toSend.key) {
        setConfirmed(true)
        setToSend(undefined)
      }
    }
  }, [sent, toSend, isReadyToVerifyConfirmation, consumeRelayConfirmation])

  const sendMessage: SendPostableMessage = (ts, tir) => {
    setToSend(ts)
    setTargetIframeRef(tir)
  }

  const consumeConfirmation = confirmed
    ? () => {
        setConfirmed(false)
        return confirmed
      }
    : undefined

  return [sendMessage, consumeConfirmation]
}

export default usePostMessage
